import { useEffect, useMemo, useState } from 'react';
import type { ChangeEvent, ReactNode } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Grid,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  Assessment as AssessmentIcon,
  Download as DownloadIcon,
  EventAvailable as EventAvailableIcon,
  Groups as GroupsIcon,
  Payments as PaymentsIcon,
  QrCodeScanner as QrCodeScannerIcon,
  TrendingUp as TrendingUpIcon,
  WarningAmber as WarningAmberIcon,
} from '@mui/icons-material';
import { collection, getDocs, query, where } from 'firebase/firestore';
import type { DocumentData, QuerySnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import { useTenant } from '../providers/TenantProvider';
import {
  computeReportingSummary,
  defaultReportRange,
  endOfDay,
  formatCurrency,
  formatDateInput,
  formatPercent,
  startOfDay,
  toDate,
  type BreakdownItem,
  type ReportingAppointment,
  type ReportingCheckIn,
  type ReportingData,
  type ReportingInvoice,
  type ReportingLead,
  type ReportingMember,
} from '../lib/reporting';

interface MetricProps {
  label: string;
  value: string;
  helper?: string;
  icon: ReactNode;
  tone?: 'default' | 'warning' | 'success';
}

function Metric({ label, value, helper, icon, tone = 'default' }: MetricProps) {
  const color = tone === 'warning' ? 'warning.main' : tone === 'success' ? 'success.main' : 'primary.main';

  return (
    <Paper sx={{ p: 2.5, height: '100%' }}>
      <Stack direction="row" spacing={1.5} alignItems="flex-start">
        <Box sx={{ color, pt: 0.5 }}>{icon}</Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" color="text.secondary">
            {label}
          </Typography>
          <Typography variant="h4" fontWeight={700}>
            {value}
          </Typography>
          {helper && (
            <Typography variant="caption" color="text.secondary">
              {helper}
            </Typography>
          )}
        </Box>
      </Stack>
    </Paper>
  );
}

function BarList({
  title,
  items,
  formatValue = (value) => value.toLocaleString(),
}: {
  title: string;
  items: BreakdownItem[];
  formatValue?: (value: number) => string;
}) {
  const visibleItems = items.filter((item) => item.value > 0);
  const max = Math.max(1, ...visibleItems.map((item) => item.value));

  return (
    <Paper sx={{ p: 3, height: '100%' }}>
      <Typography variant="h6" gutterBottom>
        {title}
      </Typography>
      <Stack spacing={1.5}>
        {visibleItems.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            No activity in this range.
          </Typography>
        )}
        {visibleItems.map((item) => (
          <Box key={item.label}>
            <Stack direction="row" justifyContent="space-between" spacing={2}>
              <Typography variant="body2" fontWeight={600} noWrap>
                {item.label}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {formatValue(item.value)}
              </Typography>
            </Stack>
            <LinearProgress
              variant="determinate"
              value={(item.value / max) * 100}
              sx={{ mt: 0.75, height: 8, borderRadius: 1 }}
            />
          </Box>
        ))}
      </Stack>
    </Paper>
  );
}

function coerceCollection<T extends { id: string }>(snapshot: QuerySnapshot<DocumentData>) {
  return snapshot.docs.map((document) => ({
    id: document.id,
    ...document.data(),
  })) as T[];
}

function formatInvoiceDate(value: unknown) {
  const date = toDate(value);
  return date ? date.toLocaleDateString() : 'No due date';
}

export function ReportsPage() {
  const { tenantId } = useTenant();
  const initialRange = defaultReportRange();
  const [dateFrom, setDateFrom] = useState(formatDateInput(initialRange.start));
  const [dateTo, setDateTo] = useState(formatDateInput(initialRange.end));
  const [data, setData] = useState<ReportingData>({
    members: [],
    leads: [],
    checkIns: [],
    invoices: [],
    appointments: [],
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!tenantId) {
      setLoading(false);
      return;
    }

    async function loadReportsData() {
      setLoading(true);
      setError(null);

      try {
        const tenantFilter = where('tenantId', '==', tenantId);
        const [membersSnap, leadsSnap, checkInsSnap, invoicesSnap, appointmentsSnap] = await Promise.all([
          getDocs(query(collection(db, 'members'), tenantFilter)),
          getDocs(query(collection(db, 'leads'), tenantFilter)),
          getDocs(query(collection(db, 'checkins'), tenantFilter)),
          getDocs(query(collection(db, 'invoices'), tenantFilter)),
          getDocs(query(collection(db, 'appointments'), tenantFilter)),
        ]);

        setData({
          members: coerceCollection<ReportingMember>(membersSnap),
          leads: coerceCollection<ReportingLead>(leadsSnap),
          checkIns: coerceCollection<ReportingCheckIn>(checkInsSnap),
          invoices: coerceCollection<ReportingInvoice>(invoicesSnap),
          appointments: coerceCollection<ReportingAppointment>(appointmentsSnap),
        });
      } catch (loadError: any) {
        console.error('Error loading reports data:', loadError);
        setError(loadError.message || 'Failed to load reports data');
      } finally {
        setLoading(false);
      }
    }

    loadReportsData();
  }, [tenantId]);

  const range = useMemo(
    () => ({
      start: startOfDay(new Date(`${dateFrom}T00:00:00`)),
      end: endOfDay(new Date(`${dateTo}T00:00:00`)),
    }),
    [dateFrom, dateTo]
  );
  const summary = useMemo(() => computeReportingSummary(data, range), [data, range]);
  const attendanceDelta = summary.attendance - summary.attendancePreviousPeriod;
  const overdueInvoices = summary.overdueInvoices.slice(0, 8);

  const handleThisMonth = () => {
    const nextRange = defaultReportRange();
    setDateFrom(formatDateInput(nextRange.start));
    setDateTo(formatDateInput(nextRange.end));
  };

  const handleLast30Days = () => {
    const end = endOfDay(new Date());
    const start = startOfDay(new Date(end.getTime() - 29 * 24 * 60 * 60 * 1000));
    setDateFrom(formatDateInput(start));
    setDateTo(formatDateInput(end));
  };

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'grey.50', py: 4 }}>
      <Container maxWidth="xl">
        <Stack spacing={3}>
          <Box>
            <Typography variant="h4" fontWeight={700} gutterBottom>
              Reports
            </Typography>
            <Typography color="text.secondary">
              Operational reporting across revenue, members, leads, attendance, and appointments.
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          <Paper sx={{ p: 2 }}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
              <TextField
                label="From"
                type="date"
                size="small"
                value={dateFrom}
                onChange={(event: ChangeEvent<HTMLInputElement>) => setDateFrom(event.target.value)}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="To"
                type="date"
                size="small"
                value={dateTo}
                onChange={(event: ChangeEvent<HTMLInputElement>) => setDateTo(event.target.value)}
                InputLabelProps={{ shrink: true }}
              />
              <Button variant="outlined" onClick={handleThisMonth}>
                This Month
              </Button>
              <Button variant="outlined" onClick={handleLast30Days}>
                Last 30 Days
              </Button>
              <Box sx={{ flexGrow: 1 }} />
              <Button variant="outlined" startIcon={<DownloadIcon />} disabled>
                Export
              </Button>
            </Stack>
          </Paper>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} lg={3}>
              <Metric
                label="Revenue Collected"
                value={formatCurrency(summary.revenueCollected)}
                helper={`${formatCurrency(summary.scheduledRevenue)} scheduled in range`}
                icon={<PaymentsIcon />}
                tone="success"
              />
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
              <Metric
                label="Overdue Balance"
                value={formatCurrency(summary.overdueBalance)}
                helper={`${summary.overdueInvoices.length} overdue invoice${summary.overdueInvoices.length === 1 ? '' : 's'}`}
                icon={<WarningAmberIcon />}
                tone={summary.overdueBalance > 0 ? 'warning' : 'default'}
              />
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
              <Metric
                label="Active Members"
                value={summary.activeMembers.toLocaleString()}
                helper={`${summary.newMembers} joined in range`}
                icon={<GroupsIcon />}
              />
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
              <Metric
                label="Attendance"
                value={summary.attendance.toLocaleString()}
                helper={`${attendanceDelta >= 0 ? '+' : ''}${attendanceDelta} vs previous period`}
                icon={<QrCodeScannerIcon />}
              />
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
              <Metric
                label="New Leads"
                value={summary.newLeads.toLocaleString()}
                helper={`${formatPercent(summary.leadConversionRate)} won in range`}
                icon={<TrendingUpIcon />}
              />
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
              <Metric
                label="Appointments"
                value={summary.appointments.toLocaleString()}
                helper={`${summary.completedAppointments} completed, ${summary.noShowAppointments} no-show`}
                icon={<EventAvailableIcon />}
              />
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
              <Metric
                label="Paused Members"
                value={summary.pausedMembers.toLocaleString()}
                helper={`${summary.canceledMembers} canceled`}
                icon={<AssessmentIcon />}
              />
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
              <Metric
                label="Canceled Appointments"
                value={summary.canceledAppointments.toLocaleString()}
                helper="Potential schedule friction"
                icon={<AssessmentIcon />}
              />
            </Grid>
          </Grid>

          <Grid container spacing={3}>
            <Grid item xs={12} lg={6}>
              <BarList title="Attendance By Day" items={summary.attendanceByDay} />
            </Grid>
            <Grid item xs={12} lg={6}>
              <BarList
                title="Revenue By Day"
                items={summary.revenueByDay}
                formatValue={(value) => formatCurrency(value)}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <BarList title="Lead Pipeline" items={summary.leadStageBreakdown} />
            </Grid>
            <Grid item xs={12} md={4}>
              <BarList title="Appointment Status" items={summary.appointmentStatusBreakdown} />
            </Grid>
            <Grid item xs={12} md={4}>
              <BarList title="Lead Sources" items={summary.topLeadSources} />
            </Grid>
          </Grid>

          <Paper sx={{ p: 3 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
              <Typography variant="h6">Overdue Invoices</Typography>
              <Chip label={summary.overdueInvoices.length} size="small" />
            </Stack>
            <Divider sx={{ mb: 2 }} />
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Invoice</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Due</TableCell>
                    <TableCell align="right">Balance</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {overdueInvoices.map((invoice) => (
                    <TableRow key={invoice.id}>
                      <TableCell>{invoice.id}</TableCell>
                      <TableCell>
                        <Chip label={invoice.status || 'unknown'} color="warning" size="small" />
                      </TableCell>
                      <TableCell>{formatInvoiceDate(invoice.dueDate)}</TableCell>
                      <TableCell align="right">{formatCurrency(invoice.amountDue ?? invoice.total ?? 0)}</TableCell>
                    </TableRow>
                  ))}
                  {overdueInvoices.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4}>
                        <Typography variant="body2" color="text.secondary">
                          No overdue invoices found.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Stack>
      </Container>
    </Box>
  );
}

export default ReportsPage;
